import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Search, CalendarDays, CalendarRange, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { UkDatePicker } from "@/components/UkDatePicker";
import { WORK_CATEGORIES, PRIORITIES } from "@/lib/workCategories";
import { toast } from "sonner";

const fld = "w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand";
const lbl = "mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground";

const emptyItem = () => ({ category: "", subcategory: "", custom: "" });

export default function BookingNew() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [team, setTeam] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState({
    booking_type: "single", vehicle_id: "", date: "", time: "09:00", end_date: "", end_time: "17:00",
    duration_minutes: 60, reason: "", priority: "normal",
    workshop_bay: "", mileage: "", assigned_to: "", notes: "",
  });
  const [workItems, setWorkItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [lookupVrm, setLookupVrm] = useState("");
  const [looking, setLooking] = useState(false);

  const loadVehicles = () => api("/vehicles").then(setVehicles).catch(() => {});
  useEffect(() => { loadVehicles(); api("/team").then(setTeam).catch(() => {}); }, []);

  const multi = form.booking_type === "multi_day";

  const loadVehicleCustomer = async (vehId, list) => {
    const v = (list || vehicles).find((x) => x.id === vehId);
    if (v?.customer_id) { try { setCustomer(await api(`/customers/${v.customer_id}`)); } catch { /* ignore */ } }
  };
  const onSelectVehicle = (vehId) => { setForm((f) => ({ ...f, vehicle_id: vehId })); if (vehId) loadVehicleCustomer(vehId); };

  const setItem = (idx, patch) => setWorkItems((items) => items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setWorkItems((items) => [...items, emptyItem()]);
  const removeItem = (idx) => setWorkItems((items) => (items.length === 1 ? [emptyItem()] : items.filter((_, i) => i !== idx)));

  const lookupVehicle = async () => {
    const vrm = lookupVrm.trim().toUpperCase().replace(/\s/g, "");
    if (!vrm) { toast.error("Enter a registration"); return; }
    setLooking(true);
    try {
      const existing = vehicles.find((v) => (v.vrm || "").toUpperCase().replace(/\s/g, "") === vrm);
      if (existing) { setForm((f) => ({ ...f, vehicle_id: existing.id })); loadVehicleCustomer(existing.id); toast.success(`Selected existing vehicle ${existing.vrm}`); return; }
      let details = {};
      try { details = await api(`/dvla/lookup/${encodeURIComponent(vrm)}`); }
      catch { toast.message("Lookup unavailable — creating a basic record you can edit later."); }
      const created = await api("/vehicles", { method: "POST", body: JSON.stringify({ vrm, make: details.make || "", model: details.model || "", colour: details.colour || null, year: details.year || null }) });
      const list = await api("/vehicles"); setVehicles(list);
      setForm((f) => ({ ...f, vehicle_id: created.id }));
      loadVehicleCustomer(created.id, list);
      toast.success(`Vehicle ${created.vrm} added and selected`);
    } catch (e) {
      if (e.status === 409) {
        const list = await api("/vehicles"); setVehicles(list);
        const again = list.find((v) => (v.vrm || "").toUpperCase().replace(/\s/g, "") === vrm);
        if (again) { setForm((f) => ({ ...f, vehicle_id: again.id })); loadVehicleCustomer(again.id, list); toast.success(`Selected existing vehicle ${again.vrm}`); return; }
      }
      toast.error(e.message || "Could not look up vehicle");
    } finally { setLooking(false); }
  };

  const buildWorkItems = () => workItems
    .filter((w) => w.category)
    .map((w) => ({ category: w.category, subcategory: w.subcategory === "__custom__" ? (w.custom.trim() || null) : (w.subcategory || null) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!customer) { toast.error("Select or add a customer"); return; }
    if (!form.vehicle_id || !form.date || !form.reason.trim()) { toast.error("Vehicle, date and requested work are required"); return; }
    const items = buildWorkItems();
    if (items.length === 0) { toast.error("Add at least one work category"); return; }
    const seen = new Set();
    for (const it of items) {
      const key = `${it.category}|${it.subcategory || ""}`;
      if (seen.has(key)) { toast.error("Duplicate category/subcategory — remove the repeated one"); return; }
      seen.add(key);
    }
    if (multi && !form.end_date) { toast.error("Expected collection date is required"); return; }
    if (multi && form.end_date < form.date) { toast.error("Collection date must be on or after the arrival date"); return; }
    setSaving(true);
    const start_at = `${form.date}T${form.time || "09:00"}:00`;
    const payload = {
      vehicle_id: form.vehicle_id, customer_id: customer.id, assigned_to: form.assigned_to || null,
      start_at, duration_minutes: Number(form.duration_minutes) || 60, reason: form.reason.trim(),
      work_items: items, work_category: items[0].category, subcategory: items[0].subcategory,
      priority: form.priority, mileage: form.mileage ? Number(form.mileage) : null,
      notes: form.notes || "", booking_type: form.booking_type,
    };
    if (multi) {
      payload.end_at = `${form.end_date}T${form.end_time || "17:00"}:00`;
      payload.workshop_bay = form.workshop_bay || null;
    }
    const post = async (force) => api(`/bookings${force ? "?force=true" : ""}`, { method: "POST", body: JSON.stringify(payload) });
    try {
      await post(false);
      toast.success(multi ? "Multi-day workshop booking created" : "Booking created");
      navigate("/calendar");
    } catch (e2) {
      if (e2.status === 409 && e2.body?.detail?.code === "overlap") {
        if (window.confirm(e2.body.detail.message)) {
          try { await post(true); toast.success("Booking created"); navigate("/calendar"); } catch (e3) { toast.error(e3.message); }
        }
      } else { toast.error(e2.message); }
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <Link to="/calendar" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Calendar</Link>
      <PageHeader title="New Booking" />
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-card p-6">
        {/* Booking type selector */}
        <div>
          <span className={lbl}>BOOKING TYPE</span>
          <div className="grid grid-cols-2 gap-2" data-testid="booking-type-toggle">
            {[["single", "Single-Day", CalendarDays], ["multi_day", "Multi-Day Workshop", CalendarRange]].map(([v, l, Icon]) => (
              <button type="button" key={v} data-testid={`btype-${v}`} onClick={() => setForm({ ...form, booking_type: v })}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-bold transition-colors ${form.booking_type === v ? "border-brand bg-brand text-white" : "border-border bg-background text-midnight"}`}>
                <Icon className="h-4 w-4" />{l}
              </button>
            ))}
          </div>
        </div>

        <div><span className={lbl}>CUSTOMER *</span><CustomerPicker value={customer} onChange={setCustomer} /></div>

        <label className="block"><span className={lbl}>VEHICLE *</span>
          <div className="mb-2 flex gap-2">
            <input value={lookupVrm} onChange={(e) => setLookupVrm(e.target.value.toUpperCase())} placeholder="Enter reg to look up…" data-testid="booking-lookup-vrm" className="flex-1 rounded-lg border border-input px-3.5 py-2.5 text-sm uppercase outline-none focus:border-brand" />
            <button type="button" onClick={lookupVehicle} disabled={looking} data-testid="booking-lookup-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-midnight px-3.5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Look Up Vehicle
            </button>
          </div>
          <select value={form.vehicle_id} onChange={(e) => onSelectVehicle(e.target.value)} data-testid="booking-vehicle" className={fld}>
            <option value="">Select…</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.vrm} — {v.make} {v.model}</option>)}</select></label>

        {/* Dates / times */}
        {!multi ? (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><span className={lbl}>DATE *</span>
              <UkDatePicker value={form.date} onChange={(d) => setForm((f) => ({ ...f, date: d }))} testId="booking-date" /></div>
            <label className="block"><span className={lbl}>TIME *</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="booking-time" className={fld} /></label>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3" data-testid="multiday-dates">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-amber-700">Workshop occupancy period</p>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={lbl}>VEHICLE ARRIVAL DATE *</span>
                <UkDatePicker value={form.date} onChange={(d) => setForm((f) => ({ ...f, date: d, end_date: f.end_date && f.end_date < d ? "" : f.end_date }))} testId="booking-arrival-date" /></div>
              <label className="block"><span className={lbl}>ARRIVAL TIME</span>
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="booking-start-time" className={fld} /></label>
              <div><span className={lbl}>EXPECTED COLLECTION *</span>
                <UkDatePicker value={form.end_date} min={form.date} onChange={(d) => setForm((f) => ({ ...f, end_date: d }))} testId="booking-collection-date" /></div>
              <label className="block"><span className={lbl}>COLLECTION TIME</span>
                <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="booking-collection-time" className={fld} /></label>
            </div>
          </div>
        )}

        {/* Priority */}
        <div>
          <span className={lbl}>BOOKING PRIORITY</span>
          <div className="grid grid-cols-3 gap-2" data-testid="booking-priority">
            {PRIORITIES.map((p) => (
              <button type="button" key={p.v} data-testid={`priority-${p.v}`} onClick={() => setForm({ ...form, priority: p.v })}
                className={`rounded-lg border px-3 py-2 text-sm font-bold transition-colors ${form.priority === p.v
                  ? (p.v === "emergency" ? "border-red-500 bg-red-500 text-white" : p.v === "urgent" ? "border-amber-500 bg-amber-500 text-white" : "border-brand bg-brand text-white")
                  : "border-border bg-background text-midnight"}`}>{p.l}</button>
            ))}
          </div>
        </div>

        {/* Work categories (multiple) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={`${lbl} mb-0`}>WORK CATEGORIES *</span>
            <button type="button" onClick={addItem} data-testid="add-work-category" className="inline-flex items-center gap-1 rounded-lg border border-brand px-2.5 py-1 text-xs font-bold text-brand-2 hover:bg-brand-tertiary/60">
              <Plus className="h-3.5 w-3.5" />Add Another Work Category
            </button>
          </div>
          {workItems.map((it, idx) => {
            const subs = (WORK_CATEGORIES.find((c) => c.id === it.category) || {}).subs || [];
            return (
              <div key={idx} className="rounded-lg border border-border bg-background/60 p-2" data-testid={`work-item-${idx}`}>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block"><span className="mb-1 block text-[10px] font-bold tracking-wide text-muted-foreground">MAIN CATEGORY</span>
                    <select value={it.category} onChange={(e) => setItem(idx, { category: e.target.value, subcategory: "", custom: "" })} data-testid={`work-category-${idx}`} className={fld}>
                      <option value="">Select…</option>
                      {WORK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select></label>
                  <label className="block"><span className="mb-1 block text-[10px] font-bold tracking-wide text-muted-foreground">SUBCATEGORY</span>
                    <select value={it.subcategory} onChange={(e) => setItem(idx, { subcategory: e.target.value })} data-testid={`work-subcategory-${idx}`} disabled={!it.category} className={fld}>
                      <option value="">{it.category ? "Select…" : "Choose a category first"}</option>
                      {subs.map((s) => <option key={s} value={s}>{s}</option>)}
                      {it.category && <option value="__custom__">Other (custom)…</option>}
                    </select></label>
                </div>
                {it.subcategory === "__custom__" && (
                  <input value={it.custom} onChange={(e) => setItem(idx, { custom: e.target.value })} data-testid={`work-custom-${idx}`} placeholder="Describe the work type" className={`${fld} mt-2`} />
                )}
                {(workItems.length > 1 || it.category) && (
                  <button type="button" onClick={() => removeItem(idx)} data-testid={`remove-work-category-${idx}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-danger hover:underline">
                    <X className="h-3.5 w-3.5" />Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <label className="block"><span className={lbl}>REQUESTED WORK / PROBLEM *</span>
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="booking-reason" placeholder="e.g. Front-end accident repair, knocking noise" className={`${fld} text-base`} /></label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={lbl}>{multi ? "EST. WORKING TIME (MIN)" : "DURATION (MINUTES)"}</span>
            <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="booking-duration" placeholder={multi ? "optional" : ""} className={fld} /></label>
          <label className="block"><span className={lbl}>MILEAGE</span>
            <input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="booking-mileage" placeholder="optional" className={fld} /></label>
        </div>

        {multi && (
          <label className="block"><span className={lbl}>WORKSHOP BAY</span>
            <input value={form.workshop_bay} onChange={(e) => setForm({ ...form, workshop_bay: e.target.value })} data-testid="booking-bay" placeholder="optional (e.g. Bay 2, Bodyshop)" className={fld} /></label>
        )}

        <label className="block"><span className={lbl}>ASSIGNED {multi ? "MECHANIC / TECHNICIAN" : "TO"}</span>
          <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="booking-assigned" className={fld}>
            <option value="">Unassigned</option>{team.filter((m) => m.active !== false).map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}</select></label>

        <label className="block"><span className={lbl}>INTERNAL NOTES</span>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" rows={2} placeholder="Staff-only notes (not shown to the customer)" className={fld} /></label>

        {multi && <p className="text-[11px] text-muted-foreground">This reserves the vehicle&apos;s stay at the garage only — it does not block MOT, service, diagnostics or online bookings on these dates.</p>}

        <button type="submit" disabled={saving} data-testid="booking-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "CREATE BOOKING"}
        </button>
      </form>
    </div>
  );
}
